import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Text } from '@mantine/core';
import { Commodities } from '../../shared/types';
import useValidatedSearchParams from '../../router/useValidatedSearchParams';
import { MultiSelectInput } from '../../shared/components/Inputs/Inputs';
import { FileInput } from '../../shared/components/Inputs/FileInput';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUploadPlots } from './useUploadPlots';
import { showNotification } from '@mantine/notifications';
import { BetaIcon } from '../../../lib/components';
import { useTracking } from '../../hooks';

type FormValues = {
  files: File[];
  commodities: string[];
};

const schema = yup.object().shape({
  files: yup.array().min(1, 'Please upload at least one file'),
  commodities: yup.array().min(1, 'Please select at least one commodity'),
});

export const PlotImportForm = () => {
  const { t } = useTranslation();
  const { setValidatedParam } = useValidatedSearchParams();
  const { trackEvent } = useTracking();

  const { plotsWithErrors, successfulUploads, isUploading, uploadPlots } =
    useUploadPlots({
      context: 'Map',
      onSuccess: () => {
        setValidatedParam('import-modal', null);
        showNotification({
          color: 'green',
          title: t('plots.import.success'),
          message: t('plots.import.successInfo'),
        });

        trackEvent('Map', {
          step: 'import-plots',
          action: 'upload-plots-success',
        });
      },
    });

  const defaultValues = {
    files: [],
    commodities: [],
  };

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const commoditiesOptions = Object.entries(Commodities).map(
    ([label, value]) => ({
      value,
      label: t(`common.commodities.${label}`),
    })
  );

  const onSubmit = async (data: FormValues) => {
    await uploadPlots(data.files, data.commodities);
  };

  return (
    <FormProvider {...form}>
      <Flex p="md" direction="column" gap="21px">
        <Text size="sm" mb="16px">
          {t('plots.import.description')}
        </Text>

        <FileInput
          name="files"
          accept={{
            'application/geo+json': ['.geojson'],
          }}
          label={t('plots.import.dropFiles')}
        />

        <MultiSelectInput
          name="commodities"
          label={t('plots.import.commodity')}
          placeholder={t('plots.import.selectCommodity')}
          data={commoditiesOptions}
          clearable
        />
        {!!plotsWithErrors?.length && (
          <Flex direction="column" gap="md">
            <Flex justify="space-between">
              <Text>{t('plots.import.fileUploading')}</Text>
              <Flex gap="sm">
                {!!plotsWithErrors?.length && (
                  <Text c="red" component="span">
                    {t('plots.import.failed', {
                      count: plotsWithErrors?.length,
                    })}
                  </Text>
                )}
                {!!successfulUploads?.length && (
                  <Text c="green.1" component="span">
                    {t('plots.import.successful', {
                      count: successfulUploads?.length,
                    })}
                  </Text>
                )}
              </Flex>
            </Flex>

            {plotsWithErrors?.map((plot, index) => (
              <Flex key={plot.filename + index} direction="column">
                <Text c="red">{plot.filename}</Text>
                <Text c="red" size="xs">
                  {plot?.errors?.join(' ')}
                </Text>
              </Flex>
            ))}

            {successfulUploads?.map((plot) => (
              <Flex key={plot.filename} justify="space-between">
                {plot.filename}
                <BetaIcon icon="check" size="sm" color="green.1" />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
      <Flex
        gap="sm"
        justify="end"
        mt="32px"
        pos="sticky"
        bottom="0"
        bg="dark.7"
        p="sm"
      >
        <Button
          variant="outline"
          type="button"
          onClick={() => setValidatedParam('import-modal', null)}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="outline"
          type="submit"
          onClick={form.handleSubmit(onSubmit)}
          loading={isUploading}
          disabled={!form.formState.isDirty}
        >
          {t('common.import')}
        </Button>
      </Flex>
    </FormProvider>
  );
};

export default PlotImportForm;
